import { useEffect, useState } from "react";
import { Heading } from "react-aria-components";

import { Button } from "../../button";
import { Modal } from "../../modal";
import { TextField } from "../../text-field";
import type { VideoPlatform } from "../nodes/video-embed-node";

interface VideoEmbedModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onInsert: (
    videoUrl: string,
    videoId: string,
    platform: VideoPlatform,
  ) => void;
}

function parseVideoUrl(url: string): {
  videoId: string;
  platform: VideoPlatform;
} | null {
  // YouTube patterns
  const youtubePatterns = [
    /(?:youtube\.com\/watch\?v=)([a-zA-Z0-9_-]{11})/,
    /(?:youtu\.be\/)([a-zA-Z0-9_-]{11})/,
    /(?:youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /(?:youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/,
  ];

  for (const pattern of youtubePatterns) {
    const match = url.match(pattern);
    if (match?.[1]) {
      return { videoId: match[1], platform: "youtube" };
    }
  }

  // Vimeo patterns
  const vimeoPatterns = [
    /(?:vimeo\.com\/)(\d+)/,
    /(?:player\.vimeo\.com\/video\/)(\d+)/,
  ];

  for (const pattern of vimeoPatterns) {
    const match = url.match(pattern);
    if (match?.[1]) {
      return { videoId: match[1], platform: "vimeo" };
    }
  }

  return null;
}

export function VideoEmbedModal({
  isOpen,
  onOpenChange,
  onInsert,
}: VideoEmbedModalProps) {
  const [url, setUrl] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setUrl("");
      setError(null);
    }
  }, [isOpen]);

  const handleSubmit = () => {
    const parsed = parseVideoUrl(url.trim());
    if (!parsed) {
      setError("Please enter a valid YouTube or Vimeo URL");
      return;
    }
    onInsert(url.trim(), parsed.videoId, parsed.platform);
    setUrl("");
    setError(null);
    onOpenChange(false);
  };

  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange} size="md">
      <div className="p-6">
        <Heading className="mb-4 text-lg font-semibold" slot="title">
          Embed Video
        </Heading>
        <div className="space-y-4">
          <TextField
            label="Video URL"
            value={url}
            onChange={(value) => {
              setUrl(value);
              setError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSubmit();
              }
            }}
            placeholder="https://www.youtube.com/watch?v=... or https://vimeo.com/..."
            autoFocus
            description="Supports YouTube and Vimeo URLs"
            errorMessage={error ?? undefined}
            isInvalid={!!error}
          />
          <div className="flex justify-end gap-2">
            <Button onPress={() => onOpenChange(false)}>Cancel</Button>
            <Button
              color="primary"
              onPress={handleSubmit}
              isDisabled={!url.trim()}
            >
              Embed
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
